import React from "react";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { AnalyticsError } from "../../../../types/onboardingAnalytics";

interface SectionFrameProps {
  title: string;
  /** Optional line under the title — used to state a section's known limits. */
  caveat?: string;
  loading: boolean;
  error: AnalyticsError | null;
  onRetry: () => void;
  /** True when the request succeeded but there is nothing to show. */
  isEmpty?: boolean;
  /** What to say when there is nothing — never a blank area (FR-038). */
  emptyMessage?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The loading / error / empty / content wrapper every dashboard section sits in.
 *
 * Two behaviours are deliberate:
 *
 * - A section fails on its own and says so on its own. One failed request must
 *   never blank the dashboard or hide the sections that did load (FR-034).
 * - A permission refusal reads differently from a transient failure and offers
 *   no retry, because retrying can never succeed (FR-035a).
 */
const SectionFrame: React.FC<SectionFrameProps> = ({
  title,
  caveat,
  loading,
  error,
  onRetry,
  isEmpty = false,
  emptyMessage = "No data yet.",
  right,
  children,
}) => (
  <Card className="shadow-2 h-full">
    <div className="flex align-items-start justify-content-between gap-3 mb-3">
      <div>
        <h3 className="m-0 text-lg font-semibold text-900">{title}</h3>
        {caveat && (
          <p className="mt-1 mb-0 text-sm text-500 line-height-3">{caveat}</p>
        )}
      </div>
      {right}
    </div>

    {loading && (
      <div className="flex flex-column gap-2" data-testid="section-loading">
        <Skeleton height="1.5rem" />
        <Skeleton height="1.5rem" width="80%" />
        <Skeleton height="1.5rem" width="60%" />
      </div>
    )}

    {!loading && error && (
      <div className="flex flex-column align-items-start gap-3">
        <Message
          severity={error.kind === "forbidden" ? "warn" : "error"}
          text={error.message}
        />
        {/* No retry on a refusal we cannot retry out of. */}
        {error.kind === "transient" && (
          <Button
            label="Retry"
            icon="pi pi-refresh"
            size="small"
            outlined
            onClick={onRetry}
          />
        )}
      </div>
    )}

    {!loading && !error && isEmpty && (
      <p className="text-500 m-0">{emptyMessage}</p>
    )}

    {!loading && !error && !isEmpty && children}
  </Card>
);

export default SectionFrame;
